import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { colors } from "../utils/theme.js";
import { api } from "../api/client.js";
import { primaryBtn } from "../clients/ui.js";
import WorkList from "../clients/WorkList.jsx";

// Sawtooth Internal (spec 2026-10-27 §C): every objective and task not tied to a client, in the
// same list (inline edits, drawer, bulk edits, template import) as a client's Objectives & Tasks.
// Internal tasks with no objective — or one that no longer exists — sit under "No objective".
const objectiveFromHash = () => {
  try { return new URLSearchParams(window.location.hash.replace(/^#/, "")).get("objective"); } catch { return null; }
};

export default function InternalPage({ users }) {
  const [objectives, setObjectives] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [toast, setToast] = useState(null);
  const [initialObjectiveId] = useState(objectiveFromHash);

  const flash = useCallback((type, text) => { setToast({ type, text }); setTimeout(() => setToast((t) => (t && t.text === text ? null : t)), 5000); }, []);
  // A failed refresh keeps what's on screen (and says so); only a failed first load shows Retry.
  const loadedRef = useRef(false);
  const load = useCallback(async () => {
    if (!loadedRef.current) setState("loading");
    const [objs, ts] = await Promise.all([api.objectives.listAll({ internal: 1 }), api.tasks.listAll({ internal: 1 })]);
    if (!objs || !ts) {
      if (loadedRef.current) flash("error", "Couldn’t refresh internal work.");
      else setState("error");
      return;
    }
    loadedRef.current = true;
    setObjectives(objs); setTasks(ts); setState("ready");
  }, [flash]);
  useEffect(() => { load(); }, [load]);
  const looseTasks = useMemo(() => {
    const ids = new Set(objectives.map((o) => o.id));
    return tasks.filter((t) => !t.objectiveId || !ids.has(t.objectiveId));
  }, [objectives, tasks]);
  const work = useMemo(() => ({ current: { objectives }, others: [], earlier: [] }), [objectives]);

  return (
    <div>
      <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 14 }}>Internal objectives and tasks — everything not tied to a client.</div>
      {state === "loading" && <div style={{ color: colors.textDim, fontSize: 13 }}>Loading…</div>}
      {state === "error" && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, color: colors.danger, fontSize: 13 }}>
          Couldn’t load internal work.
          <button onClick={load} style={primaryBtn}>Retry</button>
        </div>
      )}
      {state === "ready" && (
        <WorkList clientId={null} current={null} work={work} initialObjectiveId={initialObjectiveId} users={users}
          objectives={objectives} tasks={tasks} setObjectives={setObjectives} setTasks={setTasks} clientName="Sawtooth"
          flash={flash} reload={load} looseTasks={looseTasks} />
      )}
      {toast && (
        <div onClick={() => setToast(null)} style={{ position: "fixed", bottom: 24, right: 24, zIndex: 1200, maxWidth: 360, padding: "12px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600, color: "#fff", background: toast.type === "error" ? colors.danger : colors.success, boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>{toast.text}</div>
      )}
    </div>
  );
}
